<div class="max-w-2xl mx-auto p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
        <div>
            <h1 class="text-2xl font-semibold text-gray-900">Edit Organization</h1>
            <p class="text-sm text-gray-600 mt-1">Update organization details and billing address</p>
        </div>
        <button type="button" (click)="onCancel()"
            class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
            <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
        </button>
    </div>

    <!-- Loading State -->
    @if (loading) {
    <div class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
    }

    <!-- Success Message -->
    @if (successMessage) {
    <div class="mb-6">
        <jjames-alert type="success" [dismissible]="true" (dismissed)="onClearSuccess()">
            {{ successMessage }}
        </jjames-alert>
    </div>
    }

    <!-- Error Message -->
    @if (errorMessage) {
    <div class="mb-6">
        <jjames-alert type="error" [dismissible]="true" (dismissed)="onClearError()">
            {{ errorMessage }}
        </jjames-alert>
    </div>
    }

    @if (!loading) {
    <!-- Form -->
    <jjames-card>
        <form [formGroup]="orgForm" (ngSubmit)="onSubmit()" class="space-y-6 p-6">
            <!-- Organization Details -->
            <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Organization Details</h3>

            <div>
                <jjames-input inputId="name" label="Organization Name" type="text" formControlName="name"
                    [required]="true" [variant]="isFieldInvalid('name') ? 'error' : 'default'"
                    [errorMessage]="getFieldError('name')" placeholder="Enter organization name"
                    [helpText]="canRenameOrganization() ? '' : 'Only an administrator can change the organization name.'">
                </jjames-input>
            </div>

            <div>
                <label for="description" class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea id="description" formControlName="description" rows="3"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Optional description"></textarea>
            </div>

            <!-- Billing Address -->
            <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 pt-4">Billing Address</h3>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <jjames-input inputId="firstAddressLine" label="Address Line 1" type="text"
                        formControlName="firstAddressLine" placeholder="Street address">
                    </jjames-input>
                </div>
                <div>
                    <jjames-input inputId="secondAddressLine" label="Address Line 2" type="text"
                        formControlName="secondAddressLine" placeholder="Apt, suite, etc.">
                    </jjames-input>
                </div>
                <div>
                    <jjames-input inputId="locality" label="City" type="text" formControlName="locality"
                        placeholder="City">
                    </jjames-input>
                </div>
                <div>
                    <jjames-input inputId="postCode" label="Post Code" type="text" formControlName="postCode"
                        placeholder="Post code">
                    </jjames-input>
                </div>
                <div>
                    <jjames-input inputId="region" label="Region" type="text" formControlName="region"
                        placeholder="State / Province">
                    </jjames-input>
                </div>
                <div>
                    <!-- The backend validates a 2-letter ISO code, so this is a dropdown rather than a
                         text field: a typed "Osterreich" or "AUT" fails validation silently and the
                         address saves with no country at all. -->
                    <label for="countryCode" class="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <select id="countryCode" formControlName="countryCode"
                        class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm">
                        <option value="">Not specified</option>
                        @for (country of countries; track country.code) {
                        <option [value]="country.code">{{ country.name }} ({{ country.code }})</option>
                        }
                    </select>
                </div>
            </div>

            <!-- Form Actions -->
            <div class="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                <jjames-button type="button" variant="secondary" (clicked)="onCancel()" [disabled]="isSubmitting">
                    Cancel
                </jjames-button>
                <jjames-button type="submit" variant="primary" [disabled]="isSubmitting || orgForm.invalid"
                    iconLeft="fas fa-save">
                    @if (isSubmitting) {
                    Saving...
                    } @else {
                    Save Changes
                    }
                </jjames-button>
            </div>
        </form>
    </jjames-card>
    }
</div>